import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiService } from './api.service';
import { SessionStatus } from 'src/app/constants/session';
import { StoreNames } from 'src/app/constants/storage-names';
import { SessionService } from './storage/session.service';
import { EnrollmentBenefitCategory, EnrollmentBenefitCategoryItem, EnrollmentWaiveBenefits } from '../models/enrollment-category';
import { EnrollmentCategoryOptionManager } from '../models/enrollment-option';
import { BenefitWizardControlPanel } from '../models/enrollment-benefit-wizard';
import { EnrollmentManager } from '../models/enrollment-manager';
import { CheckoutComplete } from '../models/enrollment-checkout';
import { IListItem, ListItem } from '../models/listItem';
import { WizardPanelTitleAddList, WizardPanelTitleBuyUpList, WizardPanelTitleCancelList, WizardPanelTitleCheckoutList, WizardPanelTitleFSAList, WizardPanelTitleTerminateList, WizardPanelTitleWaiveList } from '../constants/wizard-panel-titles';
import { BenefitWizardType } from '../constants/wizard-panels';
import { OnboardingEnrollmentsItem } from '../models/enrollment-onboard';

const controllerRoute = '/enrollment-dashboard';
const controllerRouteEnrollment = '/enrollment';

@Injectable()
export class EnrollmentManagerService {
    public $BackPanelBenefitWizard = new BehaviorSubject<boolean>(null);
    public $CancelBenefitWizard = new BehaviorSubject<boolean>(null);
    public $EnrollmentComplete = new BehaviorSubject<CheckoutComplete>(null);
    public $UpdateTimeline = new BehaviorSubject<number>(null);
    public $TimelineSelectedCategory = new BehaviorSubject<EnrollmentBenefitCategoryItem>(null);
    public $EnrollmentStepper = new BehaviorSubject<ListItem[]>(null);
    public $ReloadEnrollmentStep = new BehaviorSubject<boolean>(null);
    public $GoBackEnrollmentStep = new BehaviorSubject<boolean>(null);
    public $ReloadCoverageTierGirdByDetailId = new BehaviorSubject<number>(null);
    public benefitWizardControlPanel: BenefitWizardControlPanel;
    public waiveBenefits: EnrollmentWaiveBenefits;
    public cancelHideIntro: boolean;
    public manager: EnrollmentManager;
    public sessionStore: Observable<any>;
    public enrollmentStepper: ListItem[];

    public checkoutItem: EnrollmentBenefitCategoryItem = {
        enrollmentHTML: null,
        id: -256,
        name: 'Checkout',
        isRequired: true,
        isCompleted: false,
        isWaived: false,
        materialIconName: null,
        isDisabled: false,
    };

    private ignoreUpdateEnrollmentStepper: boolean;

    constructor(
        private apiService: ApiService,
        private sessionService: SessionService,
    ) {
        this.sessionStore = this.sessionService.$SessionClear;
        this.sessionStore.subscribe((status: string) => {
            switch (status) {
                case SessionStatus.initalize:
                    let sessionState = this.sessionService.retrieve(StoreNames.enrollmentManager);
                    if (sessionState)
                        this.manager = sessionState;
                    break;

                case SessionStatus.clearMember:
                    this.manager = null;
                    this.resetBehaviorSubjects();
                    this.sessionService.remove(StoreNames.enrollmentManager);
                    break;

                case SessionStatus.clearSession:
                    this.manager = null;
                    this.resetBehaviorSubjects();
                    break;
            }
        });
    }

    public setCacheManager(): void {
        this.sessionService.store(StoreNames.enrollmentManager, this.manager);
    }

    public resetEnrollmentManagerStore(): void {
        this.benefitWizardControlPanel = null;
        this.manager = null;
        this.sessionService.remove(StoreNames.enrollmentManager);
    }

    public resetBehaviorSubjects(): void {
        this.$BackPanelBenefitWizard = new BehaviorSubject<boolean>(null);
        this.$CancelBenefitWizard = new BehaviorSubject<boolean>(null);
        this.$EnrollmentComplete = new BehaviorSubject<CheckoutComplete>(null);
        this.$UpdateTimeline = new BehaviorSubject<number>(null);
        this.$TimelineSelectedCategory = new BehaviorSubject<EnrollmentBenefitCategoryItem>(null);
        this.$EnrollmentStepper = new BehaviorSubject<ListItem[]>(null);
        this.$ReloadCoverageTierGirdByDetailId = new BehaviorSubject<number>(null);
        this.$ReloadEnrollmentStep = new BehaviorSubject<boolean>(null);
    }

    public setEnrollmentMode(mode: OnboardingEnrollmentsItem): void {
        this.resetBehaviorSubjects();
        this.manager = new EnrollmentManager();
        this.manager.enrollmentMode = mode;
        this.setCacheManager();
    }

    public getEnrollmentCategoires(): Observable<EnrollmentBenefitCategory> {
        return this.apiService.get(`${controllerRoute}/company-categories`).pipe(
            map((response: EnrollmentBenefitCategory) => {
                return response;
            }),
        );
    }

    public getEnrollmentCategoryOptions(id: number): Observable<EnrollmentCategoryOptionManager> {
        return this.apiService.post(`${controllerRoute}/category-option-manager`, { categoryId: id }).pipe(
            map((response: EnrollmentCategoryOptionManager) => {
                if(response.enrollmentCategoryOptionsModel.isAddWaiveBenefitAvailable && response.enrollmentCategoryOptionsModel.isWaiveExistingBenefitOrRequestAvailable)
                    response.enrollmentCategoryOptionsModel.isAddWaiveBenefitAvailable = false;
                this.sessionService.versionSetGuard(response.version);
                return response;
            }),
        );
    }

    public updateEnrollmentStepper(): void {
        if (this.ignoreUpdateEnrollmentStepper) {
            this.ignoreUpdateEnrollmentStepper = false;
            return;
        }

        let panelListItems: IListItem[];
        let selectedPanelTitle: IListItem;

        switch (this.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Add:
            case BenefitWizardType.WaiveBenefit:
                panelListItems = WizardPanelTitleAddList;
                break;
            case BenefitWizardType.BuyUp:
                panelListItems = WizardPanelTitleBuyUpList;
                break;
            case BenefitWizardType.Cancel:
                panelListItems = WizardPanelTitleCancelList;
                break;
            case BenefitWizardType.FSA:
                panelListItems = WizardPanelTitleFSAList;
                break;
            case BenefitWizardType.Term:
                panelListItems = WizardPanelTitleTerminateList;
                break;
            case BenefitWizardType.Waive:
                panelListItems = WizardPanelTitleWaiveList;
                break;
            case BenefitWizardType.Checkout:
                panelListItems = WizardPanelTitleCheckoutList;
                break;
        }

        if (panelListItems) {
            panelListItems.every((panelTitle: IListItem) => {
                if (panelTitle.value == this.benefitWizardControlPanel.panel) {
                    selectedPanelTitle = panelTitle;
                    return false;
                }
                return true;
            });

            if (selectedPanelTitle) {
                if (!this.enrollmentStepper)
                    this.resetEnrollmentStepper();

                this.enrollmentStepper.push(selectedPanelTitle);
                this.$EnrollmentStepper.next(this.enrollmentStepper);
            }
        }
    }

    public changeStep(step: string): void {
        let l_Index: number;
        this.enrollmentStepper.every((enrollmentStep: ListItem, index) => {
            if (enrollmentStep.value == step) {
                l_Index = index + 1;
                return false;
            }
            return true;
        });
        this.enrollmentStepper.splice(l_Index);
        this.benefitWizardControlPanel.panel = step;

        this.$EnrollmentStepper.next(this.enrollmentStepper);

        this.ignoreUpdateEnrollmentStepper = true;
        // this.$ReloadEnrollmentStep.next(true);
        this.$GoBackEnrollmentStep.next(true);
    }

    // We need to reset the BehaviorSubject to reset the subscribitons on the pages.
    // If we do not reset the BehaviorSubject after each wizard category, the subscritpions in memory will multiply.
    public resetEnrollmentStepper(): void {
        this.$ReloadEnrollmentStep = new BehaviorSubject<boolean>(null);
        this.$GoBackEnrollmentStep = new BehaviorSubject<boolean>(null);
        this.enrollmentStepper = new Array<ListItem>();
    }
}